import { replaceRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DropdownButton, FormControl } from 'react-bootstrap';
import Empty from '../../components/Empty.jsx';
import {
  employeesContent,
  loadingContent,
  noResultsContent,
} from '../../components/EmptyStates.jsx';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import { PlusButtonIcon } from '../../components/icons/PlusButtonIcon.jsx';
import { EditEmployeesLink } from '../../components/links/index.jsx';
import CustomMenuItem from '../../components/CustomMenuItem.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import {
  selectCurrentOrganizationId,
  selectUserPermissions,
} from '../../modules/auth.js';
import EmployeesTable from '../../components/employees/EmployeesTable/EmployeesTable.jsx';
import {
  deleteEmployee,
  retrieveEmployeeStatus,
  searchEmployees,
  selectSearchEmployeesState,
  selectSearchResult,
} from '../../modules/employees.js';
import { EditBlueIcon } from '../../components/icons/EditBlueIcon.jsx';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import DownloadReportIcon from '../../components/icons/DownloadReportIcon.jsx';
import EmployeeFormContainer from '../employees/EmployeeFormContainer.jsx';
import AuthorizedAction from '../../components/AuthorizedAction.js';
import { retrieveOrgGroups } from '../../modules/orgGroups.js';
import {
  hideOrgStructureUploadError,
  retrieveOrganization,
  selectOrganization,
} from '../../modules/organizations.js';
import NotificationContainer from '../notification/NotificationsContainer.jsx';
import { updateAutoHiddenNotificationState } from '../../modules/notifications.js';
import { getOrgTermsContent } from '../../content/utils.js';
import CustomPagination from '../../components/CustomPagination.jsx';

const mapStateToProps = (state, ownProps) => {
  return {
    organizationId: selectCurrentOrganizationId(state),
    organization: selectOrganization(state),
    permissions: selectUserPermissions(state),
    employees: selectSearchResult(state),
    retrieveSuccess: selectSearchEmployeesState(state).success,
    apiUrl: state.config.apiUrl,
    searchParams: selectSearchEmployeesState(state).searchParams,
  };
};

const mapDispatchToProps = {
  searchEmployees,
  deleteEmployee,
  retrieveOrganization,
  retrieveEmployeeStatus,
  retrieveOrgGroups,
  updateAutoHiddenNotificationState,
  hideOrgStructureUploadError,
};

const EmployeesContainer = ({
  searchEmployees,
  retrieveOrgGroups,
  retrieveEmployeeStatus,
  retrieveOrganization,
  hideOrgStructureUploadError,
  updateAutoHiddenNotificationState,
  searchParams,
  employees,
  retrieveSuccess,
  deleteEmployee,
  organizationId,
  organization,
  apiUrl,
}) => {
  const [isStructureEmpty, setIsStructureEmpty] = useState(false);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editableEmployeeId, setEditableEmployeeId] = useState(null);
  const [searchStr, setSearchStr] = useState('');

  const { loading, downloadBlob } = useFetchFile();

  const onHideError = () => {
    if (!window.confirm('Are you sure you want to hide this error message?'))
      return;
    hideOrgStructureUploadError(organization.id).then(() =>
      retrieveOrganization(organization.id)
    );
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchStr(query);
    searchEmployees({ ...searchParams, page: 1, search: query });
  };
  const onHideEmployeeModal = () => {
    setShowEmployeeModal(false);
    setEditableEmployeeId(null);
  };
  const onShowEmployeeModal = (id) => {
    id && setEditableEmployeeId(id);
    setShowEmployeeModal(true);
  };

  const onDeleteEmployee = (employee) => {
    retrieveEmployeeStatus(employee.id).then(({ showAlert }) => {
      const msg = !showAlert
        ? 'Are you sure you want to delete this employee?'
        : 'Are you sure you want to delete this employee?\n' +
          `This Employee is currently being tracked and used in one or more ${getOrgTermsContent('projects')} and reports. When you delete the employee at the organization level, it will be deleted across all ${getOrgTermsContent('projects')} and no longer available for selection in reports.`;

      if (!window.confirm(msg)) return;

      deleteEmployee(employee.id).then(() => {
        searchEmployees({ ...searchParams, page: 1, search: '' }).then(
          ({ result }) => setIsStructureEmpty(!result.employees.length)
        );
        setEditableEmployeeId(null);
        setSearchStr('');
      });
    });
  };

  const onAddEmployeeSubmitSuccess = () => {
    searchEmployees({ ...searchParams, page: 1, search: '' }).then(
      ({ result }) => setIsStructureEmpty(!result.employees.length)
    );
    retrieveOrganization(organizationId);
    setSearchStr('');
    setEditableEmployeeId(null);
    updateAutoHiddenNotificationState({
      isShown: true,
      type: 'success',
      forPath: '/organization/employees',
      message: `Employee was ${editableEmployeeId ? 'updated' : 'created'} successfully.`,
    });
  };

  const onDownloadErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organization.id}/group_structure_errors_csv`,
      null,
      'errors-organization-structure CSV',
      'csv'
    );
  };

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/group_structure_csv`,
      null,
      'current-group-structure CSV',
      'csv'
    );
  };

  const renderTable = () => {
    if (!retrieveSuccess) {
      return <Empty simplified content={loadingContent}></Empty>;
    }
    if (retrieveSuccess && isStructureEmpty) {
      return (
        <Empty
          content={employeesContent}
          action={() => replaceRoute('organization/employees/edit')}
          actionAuthorization={{
            action: 'update',
            subject: 'employee',
          }}
          addBtn
        ></Empty>
      );
    }

    if (retrieveSuccess && !employees.length && searchParams.search?.length) {
      return <Empty simplified content={noResultsContent} addBtn></Empty>;
    }

    return (
      <React.Fragment>
        <EmployeesTable
          employees={employees}
          onEdit={onShowEmployeeModal}
          onDelete={onDeleteEmployee}
        />
        {searchParams.totalPages > 1 ? (
          <div className="flex flex-end component component-md component-bg-white">
            <CustomPagination
              totalPages={searchParams.totalPages}
              currentPage={searchParams.page}
              action={(val) => searchEmployees({ ...searchParams, page: val })}
            />
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  useEffect(() => {
    searchEmployees({ ...searchParams, page: 1, search: '' }).then(
      ({ result }) => !result.employees.length && setIsStructureEmpty(true)
    );
    retrieveOrgGroups();
    retrieveOrganization(organizationId);
  }, []);

  return (
    <React.Fragment>
      <OrganizationHeader organizationTabs />

      <SubHeader flexJustify small>
        <div className="flex">
          <h2 className="m-r-16">Employees</h2>
          <strong className="bordered text-muted">
            Total:&nbsp;
            <span className="count" style={{ color: '#27B0E0' }}>
              {searchParams.totalObjectsPerOrganization}
            </span>
          </strong>
        </div>
        <div className="flex flex-end" style={{ width: '50%' }}>
          <div
            className="m-r-16 m-l-12"
            style={{ width: '100%', maxWidth: '540px' }}
          >
            <FormControl
              placeholder="Search"
              type="text"
              onChange={handleSearch}
              value={searchStr}
            />
          </div>
          <AuthorizedAction action="create" subject="employee">
            <Button bsStyle="success" onClick={() => onShowEmployeeModal()}>
              <PlusButtonIcon />
              Add Employee
            </Button>
          </AuthorizedAction>
          <div className="actions m-l-16">
            <DropdownButton noCaret className="btn-more btn btn-default">
              <EditEmployeesLink>
                <CustomMenuItem disabled={false}>
                  <span className="flex">
                    <EditBlueIcon />
                    <span>&nbsp;Edit</span>
                  </span>
                </CustomMenuItem>
                <CustomMenuItem divider />
              </EditEmployeesLink>
              {!isStructureEmpty ? (
                <CustomMenuItem>
                  <button
                    type="button"
                    className="flex flex-align-center w-100 h-100 p-0 btn-clear bg-transparent"
                    disabled={loading}
                    onClick={onDownloadCsv}
                  >
                    <DownloadReportIcon />
                    <span className="m-l-8">
                      {loading ? 'Loading...' : 'Download CSV'}
                    </span>
                  </button>
                </CustomMenuItem>
              ) : null}
            </DropdownButton>
          </div>
        </div>
      </SubHeader>

      {organization &&
      organization.groupsStructureErrors &&
      organization.groupsStructureErrors.error ? (
        <div className="component-xl component-border-bottom component-bg-white text-center">
          <div className="flex flex-end w-100">
            <button className="btn btn-default btn-cross" onClick={onHideError}>
              close
            </button>
          </div>
          <h3 className="flex text-danger flex-center m-b-12">
            There was an issue with the CSV.
          </h3>
          <AuthorizedAction action="update" subject="organizationStakeholder">
            <p className="no-margin text-muted">
              Please download the CSV error report
              <button
                type="button"
                className="btn-link"
                onClick={onDownloadErrorsCsv}
              >
                here
              </button>
            </p>
            <p className="text-muted">
              Changes implemented? If everything is correct, feel free to
              dismiss this alert — it won’t show up again.
            </p>
          </AuthorizedAction>
        </div>
      ) : null}

      {renderTable()}
      <EmployeeFormContainer
        employeeId={editableEmployeeId}
        showFormModal={showEmployeeModal}
        onHideFormModal={onHideEmployeeModal}
        onSubmitSuccess={onAddEmployeeSubmitSuccess}
      />

      <NotificationContainer />
    </React.Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeesContainer);
